import React, { useEffect, useState } from 'react';
import { MessageCircle, Phone, X } from 'lucide-react';
import { getCompanyInfo } from '../api/api';

/**
 * Boutons d'appel flottants.
 *
 * Sur mobile, la barre reste toujours accessible : le téléphone est le premier
 * canal de contact d'une entreprise de nettoyage, l'enterrer en bas de page
 * coûte des appels.
 */
export const FloatingContact = () => {
  const [company, setCompany] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    getCompanyInfo()
      .then(setCompany)
      .catch(() => setCompany(null));
  }, []);

  // On n'affiche qu'après un léger défilement : ne pas masquer l'appel à
  // l'action principal, tout en restant vite accessible sur mobile (peu de
  // contenu au-dessus de la ligne de flottaison).
  useEffect(() => {
    const auDefilement = () => setVisible(window.scrollY > 250);
    auDefilement();
    window.addEventListener('scroll', auDefilement, { passive: true });
    return () => window.removeEventListener('scroll', auDefilement);
  }, []);

  if (!company?.phone) return null;

  const numeroWhatsapp = company.phone.replace(/[^0-9+]/g, '').replace(/^\+/, '');
  const message = encodeURIComponent(
    `Bonjour, je souhaite un devis pour un nettoyage.`
  );

  return (
    <div
      className={`fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      {ouvert && (
        <div className="flex flex-col gap-3 mb-1">
          <a
            href={`https://wa.me/${numeroWhatsapp}?text=${message}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white shadow-lg border border-gray-100 pl-4 pr-5 py-3 rounded-full hover:shadow-xl transition-shadow"
          >
            <span className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-white" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold text-gray-900">WhatsApp</span>
          </a>

          <a
            href={`tel:${company.phone}`}
            className="flex items-center gap-3 bg-white shadow-lg border border-gray-100 pl-4 pr-5 py-3 rounded-full hover:shadow-xl transition-shadow"
          >
            <span className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-white" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold text-gray-900">Appeler</span>
          </a>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOuvert((v) => !v)}
        aria-expanded={ouvert}
        aria-label={ouvert ? 'Fermer les options de contact' : 'Nous contacter'}
        className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl flex items-center justify-center transition-colors"
      >
        {ouvert ? (
          <X className="w-6 h-6" aria-hidden="true" />
        ) : (
          <MessageCircle className="w-6 h-6" aria-hidden="true" />
        )}
      </button>
    </div>
  );
};

export default FloatingContact;
